import React, { useCallback, useMemo, useState } from 'react';
import { X, FolderOpen, Trash2, Plus, Loader2 } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import { DARK, btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from './theme';

/**
 * Reusable, named "where footage goes" presets -- a template + project root
 * + fixed variable values + which remote server(s) to also push to. Kept as
 * its own module (separate from Automations) so one destination can be
 * reused across several automations instead of being duplicated inline into
 * each one, AND so a card with no matching automation can still be assigned
 * a destination directly from the ingestion list in one pick, rather than
 * stepping through the full manual template/root/variables form every time.
 */
export default function DestinationPresetsPanel({ c, presets, templates, remoteConnections, onRefresh, onClose, embedded = false }) {
  const isDarkMode = c === DARK;
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', templateId: '', rootPath: '', templateVariables: {}, remoteConnectionIds: [], remoteTargetRoles: {}, operation: 'copy', driveMappingId: '', driveRole: 'master_backup', autoUploadDrive: false });
  const toggleFormConnectionId = (id) => setForm(prev => ({
    ...prev,
    remoteConnectionIds: prev.remoteConnectionIds.includes(id)
      ? prev.remoteConnectionIds.filter(x => x !== id)
      : [...prev.remoteConnectionIds, id],
  }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === form.templateId) || null,
    [templates, form.templateId]
  );
  // Same date-exclusion logic as automation profiles used to have inline --
  // see that reasoning where it's still explained in the UI copy below.
  const fixedVariableNames = useMemo(
    () => (selectedTemplate?.variableNames || []).filter(name => !/date/i.test(name)),
    [selectedTemplate]
  );
  const dateVariableName = useMemo(
    () => (selectedTemplate?.variableNames || []).find(name => /date/i.test(name)),
    [selectedTemplate]
  );

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = useCallback(async () => {
    if (!form.name.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await window.electronAPI.cardIngest.saveDestinationPreset({
        name: form.name.trim(),
        templateId: form.templateId || null,
        rootPath: form.rootPath || null,
        templateVariables: form.templateId ? form.templateVariables : {},
        remoteConnectionIds: form.remoteConnectionIds,
        remoteTargets: form.remoteConnectionIds.map(connectionId => ({
          connectionId,
          role: form.remoteTargetRoles[connectionId] || 'master_backup',
        })),
        operation: form.operation,
        driveMappingId: form.driveMappingId.trim() || null,
        driveRole: form.driveRole,
        autoUploadDrive: !!form.autoUploadDrive,
      });
      if (!result?.success) throw new Error(result?.error || 'Failed to save destination');
      setForm({ name: '', templateId: '', rootPath: '', templateVariables: {}, remoteConnectionIds: [], remoteTargetRoles: {}, operation: 'copy', driveMappingId: '', driveRole: 'master_backup', autoUploadDrive: false });
      setShowAdd(false);
      await onRefresh();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save destination');
    } finally {
      setSaving(false);
    }
  }, [form, saving, onRefresh]);

  const remove = useCallback(async (id) => {
    await window.electronAPI.cardIngest.deleteDestinationPreset(id);
    await onRefresh();
  }, [onRefresh]);

  const inputStyle = {
    width: '100%', padding: '6px 9px', fontSize: '12px', borderRadius: '6px',
    border: `1px solid ${c.rule}`, background: c.bg, color: c.text, fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '10px', color: c.muted, marginBottom: '3px', display: 'block' };

  const content = (
    <>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Destinations</span>
          <button onClick={onClose} style={btnStyle(c, 'ghost')}><X size={12} /></button>
        </div>
      )}
      <div style={{ fontSize: '11px', color: c.muted, marginBottom: '14px', lineHeight: 1.5 }}>
        Named "where footage goes" presets — a folder template plus which remote server(s) to also push to. Reuse one across several automations, or assign it directly to a card from the ingestion list.
      </div>

      {presets.length === 0 && !showAdd && (
        <div style={{ fontSize: '12px', color: c.muted, marginBottom: '12px' }}>No destinations configured yet.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
        {presets.map(p => (
          <div key={p.id} style={{ padding: '8px 10px', border: `1px solid ${c.rule}`, borderRadius: '7px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: '10.5px', color: c.muted, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {templates.find(t => t.id === p.templateId)?.name || 'Ask each time'}{p.rootPath ? ` — ${p.rootPath}` : ''}
                </div>
              </div>
              <button onClick={() => remove(p.id)} style={btnStyle(c, 'ghost')} title="Delete destination"><Trash2 size={12} /></button>
            </div>
            {p.remoteConnectionIds?.length > 0 && (
              <div style={{ fontSize: '10.5px', color: c.muted, marginTop: '4px' }}>
                → {(p.remoteTargets || p.remoteConnectionIds.map(connectionId => ({ connectionId, role: 'master_backup' })))
                  .map(target => target.role === 'proxy_delivery' ? 'proxy delivery' : 'master backup').join(' · ')}
              </div>
            )}
            {p.driveMappingId && <div style={{ fontSize: '10.5px', color: c.muted }}>Drive: {p.driveRole === 'proxy_delivery' ? 'proxy delivery only' : 'master backup'}</div>}
          </div>
        ))}
      </div>

      {!showAdd && (
        <button onClick={() => setShowAdd(true)} style={btnStyle(c, 'ghost')}><Plus size={12} /> Add destination</button>
      )}

      {showAdd && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${c.rule}`, borderRadius: '8px' }}>
          <div>
            <span style={labelStyle}>Name</span>
            <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Main Edit Server" style={inputStyle} />
          </div>

          <div>
            <span style={labelStyle}>Folder rule (optional — uses a saved template)</span>
            <PillDropdown
              value={form.templateId}
              onChange={(v) => setField('templateId', v)}
              isDarkMode={isDarkMode}
              options={[{ value: '', label: 'Ask each time' }, ...templates.map(t => ({ value: t.id, label: t.name }))]}
              triggerWidth="100%"
            />
          </div>

          {(
            <div>
              <span style={labelStyle}>Exact local destination folder</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input value={form.rootPath} readOnly placeholder="Not set" style={{ ...inputStyle, flex: 1, color: form.rootPath ? c.text : c.muted }} />
                <button
                  onClick={async () => {
                    const result = await window.electronAPI.cardIngest.chooseProjectRoot();
                    if (result?.success && result.path) setField('rootPath', result.path);
                  }}
                  style={btnStyle(c, 'ghost')}
                >
                  <FolderOpen size={12} /> Choose
                </button>
              </div>
            </div>
          )}

          {selectedTemplate && fixedVariableNames.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={labelStyle}>Template values (fixed every time this destination is used)</span>
              {fixedVariableNames.map(name => (
                <input
                  key={name}
                  value={form.templateVariables[name] || ''}
                  onChange={e => setForm(prev => ({ ...prev, templateVariables: { ...prev.templateVariables, [name]: e.target.value } }))}
                  placeholder={name}
                  style={inputStyle}
                />
              ))}
            </div>
          )}

          {selectedTemplate && dateVariableName && (
            <div style={{ fontSize: '10.5px', color: c.muted, lineHeight: 1.5 }}>
              "{dateVariableName}" isn't set here — it's filled in with the current date each time this destination is used, so the same day's cards land in the same folder and a new day gets a new one.
            </div>
          )}

          <div>
            <span style={labelStyle}>Delivery operation</span>
            <PillDropdown
              value={form.operation}
              onChange={(v) => setField('operation', v)}
              isDarkMode={isDarkMode}
              options={[
                { value: 'copy', label: 'Verified copy' },
                { value: 'copy_and_proxy', label: 'Verified copy + proxy' },
                { value: 'proxy_only', label: 'Proxy only' },
              ]}
              triggerWidth="100%"
            />
          </div>

          <div>
            <span style={labelStyle}>Google Drive mapping (optional)</span>
            <input value={form.driveMappingId} onChange={e => setField('driveMappingId', e.target.value)} placeholder="Companion Sync mapping ID" style={inputStyle} />
            <div style={{ marginTop: '6px' }}>
              <PillDropdown
                value={form.driveRole}
                onChange={(v) => setField('driveRole', v)}
                isDarkMode={isDarkMode}
                options={[
                  { value: 'master_backup', label: 'Master backup (original footage)' },
                  { value: 'proxy_delivery', label: 'Proxy delivery only' },
                ]}
                triggerWidth="100%"
              />
            </div>
            <label style={{ display: 'flex', gap: '7px', marginTop: '6px', fontSize: '11px' }}>
              <input type="checkbox" checked={form.autoUploadDrive} onChange={e => setField('autoUploadDrive', e.target.checked)} />
              Auto-upload through this mapping
            </label>
            <div style={{ fontSize: '10px', color: c.muted, marginTop: '4px' }}>Requires the Companion app Drive Sync bridge.</div>
          </div>

          {remoteConnections.length > 0 && (
            <div>
              <span style={labelStyle}>Also upload to (optional, any number at once)</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {remoteConnections.map(conn => (
                  <label key={conn.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.remoteConnectionIds.includes(conn.id)} onChange={() => toggleFormConnectionId(conn.id)} />
                    {conn.name}
                    {form.remoteConnectionIds.includes(conn.id) && (
                      <div style={{ marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                        <PillDropdown
                          value={form.remoteTargetRoles[conn.id] || 'master_backup'}
                          onChange={(v) => setForm(prev => ({ ...prev, remoteTargetRoles: { ...prev.remoteTargetRoles, [conn.id]: v } }))}
                          isDarkMode={isDarkMode}
                          options={[
                            { value: 'master_backup', label: 'Master backup (originals)' },
                            { value: 'proxy_delivery', label: 'Proxy delivery only' },
                          ]}
                          align="right"
                        />
                      </div>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {form.rootPath && (
            <div style={{ fontSize: '10.5px', color: c.muted, lineHeight: 1.5, padding: '8px 10px', border: `1px dashed ${c.rule}`, borderRadius: '6px' }}>
              Want this backed up to Google Drive too? Drive sync now lives in the Companion app (Sync tab) —
              add a folder mapping there pointed at <span style={{ fontFamily: 'monospace' }}>{form.rootPath}</span> and it uploads automatically,
              same as an FTP connection above but for Drive.
            </div>
          )}

          {saveError && <div style={{ fontSize: '11px', color: c.err }}>{saveError}</div>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
            <button onClick={submit} disabled={saving || !form.name.trim()} style={{ ...btnStyle(c, 'primary'), opacity: (saving || !form.name.trim()) ? 0.5 : 1 }}>
              {saving ? <Loader2 size={12} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> : null} Save destination
            </button>
            <button onClick={() => setShowAdd(false)} style={btnStyle(c, 'ghost')}>Cancel</button>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return <div style={{ width: '100%' }}>{content}</div>;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 520, maxHeight: '82vh', overflow: 'auto', background: c.bg, borderRadius: '10px', border: `1px solid ${c.rule}`, padding: '18px 20px' }}
      >
        {content}
      </div>
    </div>
  );
}
